/**
 * RBAC-5.3 · NFR-6, NFR-6b. Recovery is reachable, and degraded accounts are visible.
 *
 * Pure: parsers for the five RPCs in `20261102a5`, and the copy the blocked surface and the internal
 * view say. Who may do what is the database's; nothing here passes a tenant id except the
 * superadmin's recovery, which names the account it is acting on.
 *
 * ⚠️ THE COPY IS THE REQUIREMENT. NFR-6b: the blocked surface "names what happened and offers a route
 * out". "Ask an Admin on your account" is a route out only when the account HAS one; on an account
 * with none it sends the reader looking for a person who does not exist. So the sentence depends on
 * `my_account_administration()`, and the tests below pin each version.
 */

export const MY_ACCOUNT_ADMINISTRATION_RPC = 'my_account_administration';
export const REQUEST_ACCOUNT_RECOVERY_RPC = 'request_account_recovery';
export const DEGRADED_ACCOUNTS_RPC = 'degraded_accounts';
export const RECOVER_ACCOUNT_ADMINISTRATION_RPC = 'recover_account_administration';
export const RESOLVE_ACCOUNT_RECOVERY_REQUEST_RPC = 'resolve_account_recovery_request';

export const ACCOUNT_HEALTH_PATH = '/app/account-health';
/** One cache entry for the header badge and the page, so acting on the page updates the badge. */
export const DEGRADED_ACCOUNTS_QUERY_KEY = ['degraded-accounts'] as const;
export const SUPPORT_TICKETS_PATH = '/app/support-tickets';

export interface PersonName {
  firstName: string;
  lastName: string;
}

export interface MyAccountAdministration {
  accountName: string;
  administered: boolean;
  administrators: PersonName[];
  canRequest: boolean;
  openRequestAt: string | null;
}

export type FindingSeverity = 'critical' | 'warning';

export interface AccountFinding {
  code: string;
  severity: FindingSeverity;
  summary: string;
}

export interface AccountAdministrator extends PersonName {
  userId: string;
  email: string | null;
  lastSignInAt: string | null;
  canSignIn: boolean;
}

export interface RecoveryRequest {
  requestId: number;
  requestedByName: string | null;
  requestedByEmail: string | null;
  note: string | null;
  createdAt: string;
}

export interface RecoveryCandidate extends PersonName {
  userId: string;
  email: string | null;
  isActive: boolean;
  roleName: string | null;
}

export interface DegradedAccount {
  tenantId: string;
  accountName: string;
  parentAccountName: string | null;
  activePeople: number;
  inactivePeople: number;
  severity: FindingSeverity;
  findings: AccountFinding[];
  administrators: AccountAdministrator[];
  openRequests: RecoveryRequest[];
  candidates: RecoveryCandidate[];
  lastRecovery: { at: string; byName: string | null; resolution: string | null } | null;
}

export interface RecoveryResult {
  roleName: string;
  reactivated: boolean;
  resolvedRequests: number;
  administrators: number;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const severity = (v: unknown): FindingSeverity => (v === 'critical' ? 'critical' : 'warning');

const parseName = (v: Record<string, unknown>): PersonName => ({
  firstName: str(v.first_name).trim(),
  lastName: str(v.last_name).trim(),
});

/** RPC → view model, or null when the answer is unreadable (the surface then falls back to the generic copy). */
export function parseMyAccountAdministration(raw: unknown): MyAccountAdministration | null {
  if (!isObj(raw) || typeof raw.administered !== 'boolean') return null;
  const open = isObj(raw.open_request) ? strOrNull(raw.open_request.created_at) : null;
  return {
    accountName: str(raw.account_name).trim(),
    administered: raw.administered,
    administrators: arr(raw.administrators).filter(isObj).map(parseName),
    canRequest: raw.can_request === true,
    openRequestAt: open,
  };
}

export function parseDegradedAccounts(raw: unknown): DegradedAccount[] {
  return arr(raw)
    .filter(isObj)
    .filter((a) => typeof a.tenant_id === 'string')
    .map((a) => ({
      tenantId: a.tenant_id as string,
      accountName: str(a.account_name).trim() || 'Unnamed account',
      parentAccountName: strOrNull(a.parent_account_name),
      activePeople: num(a.active_people),
      inactivePeople: num(a.inactive_people),
      severity: severity(a.severity),
      findings: arr(a.findings)
        .filter(isObj)
        .map((f) => ({ code: str(f.code), severity: severity(f.severity), summary: str(f.summary) })),
      administrators: arr(a.administrators)
        .filter(isObj)
        .map((x) => ({
          ...parseName(x),
          userId: str(x.user_id),
          email: strOrNull(x.email),
          lastSignInAt: strOrNull(x.last_sign_in_at),
          canSignIn: x.can_sign_in === true,
        })),
      openRequests: arr(a.open_requests)
        .filter(isObj)
        .map((q) => ({
          requestId: num(q.request_id),
          requestedByName: strOrNull(q.requested_by_name),
          requestedByEmail: strOrNull(q.requested_by_email),
          note: strOrNull(q.note),
          createdAt: str(q.created_at),
        })),
      candidates: arr(a.candidates)
        .filter(isObj)
        .map((c) => ({
          ...parseName(c),
          userId: str(c.user_id),
          email: strOrNull(c.email),
          isActive: c.is_active === true,
          roleName: strOrNull(c.role_name),
        })),
      lastRecovery: isObj(a.last_recovery)
        ? {
            at: str(a.last_recovery.at),
            byName: strOrNull(a.last_recovery.by_name),
            resolution: strOrNull(a.last_recovery.resolution),
          }
        : null,
    }));
}

export function parseRecoveryResult(raw: unknown): RecoveryResult | null {
  if (!isObj(raw)) return null;
  return {
    roleName: str(raw.role_name) || 'Admin',
    reactivated: raw.reactivated === true,
    resolvedRequests: num(raw.resolved_requests),
    administrators: num(raw.administrators),
  };
}

export function personName(p: PersonName): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Someone';
}

/** "Dana Lee", "Dana Lee or Sam Ortiz", "Dana Lee, Sam Ortiz or Kim Park", "Dana Lee, Sam Ortiz or 3 others". */
export function namesToAsk(people: PersonName[]): string {
  const names = people.map(personName);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(', ')} or ${names.length - 2} others`;
}

export interface BlockedSurfaceCopy {
  title: string;
  body: string;
  /** Offer "Ask Joules to restore administration": only when nobody on the account can help. */
  offerRequest: boolean;
}

/**
 * The Roles & Permissions blocked surface (RolesNoAccess). `state` is null while unknown or when it
 * could not be read: the generic sentence is then the honest one, because it does not claim to know.
 */
export function rolesBlockedCopy(state: MyAccountAdministration | null): BlockedSurfaceCopy {
  const needs = 'Changing what roles can do needs the "Administer roles and permissions" permission.';
  if (!state) {
    return {
      title: "You don't have access to Roles & Permissions",
      body: `${needs} If you need it, ask someone on your account who administers roles.`,
      offerRequest: false,
    };
  }
  const account = state.accountName || 'your account';
  if (state.administered) {
    return {
      title: "You don't have access to Roles & Permissions",
      body: `${needs} On ${account}, ${namesToAsk(state.administrators)} can give it to you.`,
      offerRequest: false,
    };
  }
  return {
    title: `Nobody on ${account} can administer roles`,
    body:
      `${needs} Nobody on ${account} holds it right now, so there is nobody on the account to ask. ` +
      'Joules support can make someone on your account an administrator again.',
    offerRequest: state.canRequest,
  };
}

/** After a request is filed (or found already open). */
export function requestFiledCopy(createdAt: string | null, formatDate: (iso: string) => string): string {
  const when = createdAt ? ` on ${formatDate(createdAt)}` : '';
  return (
    `Joules support has your request${when}. A Joules administrator will make someone on your account ` +
    'an administrator; once they have, this page opens for them.'
  );
}

export const FINDING_LABELS: Readonly<Record<string, string>> = {
  no_administrator: 'No administrator',
  administrators_cannot_sign_in: "Administrators can't sign in",
  recovery_requested: 'Asked for help',
  single_administrator: 'One administrator',
  administrators_dormant: 'Administrators inactive',
};

export function findingLabel(code: string): string {
  return FINDING_LABELS[code] ?? code.replace(/_/g, ' ');
}

export function criticalCount(accounts: DegradedAccount[]): number {
  return accounts.filter((a) => a.severity === 'critical').length;
}

/** Whether the internal view offers "Make administrator" for this account. */
export function canRecover(account: DegradedAccount): boolean {
  return account.findings.some((f) => f.code === 'no_administrator' || f.code === 'administrators_cannot_sign_in' || f.code === 'recovery_requested')
    && account.candidates.length > 0;
}

/** What the confirm button promises, said before it is pressed. */
export function recoverConfirmCopy(account: DegradedAccount, person: RecoveryCandidate): string {
  const who = personName(person);
  const restore = person.isActive ? '' : ` Their access is removed today, so it will be restored first.`;
  return (
    `${who} will be given the Admin role on ${account.accountName}, in addition to ` +
    `${person.roleName ?? 'their current role'}, and will be able to administer roles there.` +
    restore +
    (account.openRequests.length > 0
      ? ` ${account.openRequests.length === 1 ? 'The open request' : `All ${account.openRequests.length} open requests`} will be marked resolved.`
      : ' This is recorded as a recovery found in review.')
  );
}

/** What the account has nobody to recover with. */
export function noCandidatesCopy(account: DegradedAccount): string {
  return (
    `Nobody on ${account.accountName} can be made an administrator: it has no people who are not deleted. ` +
    'Switch to the account and invite someone from Settings → Users with the Admin role.'
  );
}
